import React, { useState, useEffect, useMemo } from "react";
import { Task, TaskDependency, Project } from "@/api/types";
import { apiClient } from "@/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faSpinner, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

interface TaskListProps {
  currentTaskId?: string; // ID of the current task to exclude from the list
  currentProjectId?: string; // ID of the current project to filter tasks by
  selectedTasks?: TaskDependency[]; // Array of tasks
  setSelectedTasks?: (taskIds: TaskDependency[]) => void;
  onTaskSelect?: (task: TaskDependency) => void;
  onTaskDeselect?: (task: TaskDependency) => void;
  multiSelect?: boolean;
  className?: string;
}

export default function TaskList({
  currentTaskId,
  currentProjectId,
  selectedTasks = [],
  setSelectedTasks,
  onTaskSelect,
  onTaskDeselect,
  multiSelect = true,
  allowCrossProject = true,
  className = "",
}: TaskListProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch tasks and projects
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all tasks
        // Exclude current task if ID is provided
        // Also include only tasks from the current project if project ID is provided and allowCrossProject is false
        const tasksData = await apiClient.get<Task[]>("/tasks/")
          .then(res => res.filter(
            task => task.id != currentTaskId && (!currentProjectId || allowCrossProject || task.project == currentProjectId)
          )
        );

        setTasks(tasksData);
      } catch (err) {
        console.error("Error fetching tasks:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch tasks");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentTaskId, currentProjectId, allowCrossProject]);

  // Filter tasks based on search term
  const filteredTasks = useMemo(() => {
    if (!searchTerm.trim()) {
      return tasks;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return tasks.filter(task => 
      task.title.toLowerCase().includes(lowerSearchTerm) ||
      (task.description && task.description.toLowerCase().includes(lowerSearchTerm)) ||
      (task.projectName && task.projectName.toLowerCase().includes(lowerSearchTerm))
    );
  }, [tasks, searchTerm]);

  const handleTaskToggle = (task: TaskDependency) => {
    const isSelected = selectedTasks.some(t => t.taskId === task.taskId);
    
    if (isSelected) {
      onTaskDeselect?.(task);
    } else {
      onTaskSelect?.(task);
    }

    if (setSelectedTasks) {
      if (multiSelect) {
        if (isSelected) {
          setSelectedTasks(selectedTasks.filter(elem => elem.taskId !== task.taskId));
        } else {
          setSelectedTasks([...selectedTasks, task]);
        }
      } else {
        setSelectedTasks(isSelected ? [] : [task]);
      }
    }
  };

  const isTaskSelected = (taskId: string) => selectedTasks.some(t => t.taskId === taskId);

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-gray-500 mr-3" />
        <span className="text-gray-600">Loading tasks...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl text-red-500 mr-3" />
        <div>
          <p className="text-red-600 font-medium">Error loading tasks</p>
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Search Box */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <FontAwesomeIcon 
            icon={faSearch} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
          />
          <input
            type="text"
            placeholder="Search tasks by name or project..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </div>
        
        {/* Results count */}
        <div className="mt-2 text-sm text-gray-600">
          {filteredTasks.length} of {tasks.length} tasks
          {searchTerm && (
            <span className="ml-1">
              matching "{searchTerm}"
            </span>
          )}
          {selectedTasks.length > 0 && (
              <span className="ml-1 text-sm text-gray-600">
                ({selectedTasks.length} selected)
              </span>
          )}
        </div>
      </div>

      {/* Task List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredTasks.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm ? (
              <div>
                <p>No tasks found matching "{searchTerm}"</p>
                <p className="text-sm mt-1">Try a different search term</p>
              </div>
            ) : (
              <p>No tasks available</p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredTasks.map((task) => (
              <div
                key={task.id}
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  isTaskSelected(task.id) ? "bg-blue-50 border-l-4 border-l-blue-500" : ""
                }`}
                onClick={() => handleTaskToggle({ taskId: task.id })}
              >
                <div className="flex items-start space-x-3">
                  {/* Checkbox */}
                  {multiSelect && (
                    <input
                      type="checkbox"
                      checked={isTaskSelected(task.id)}
                      onChange={() => {}} // Handled by parent div onClick
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  )}
                  
                  {/* Task Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {task.title}
                      </h4>
                      
                      {/* Project Badge */}
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 flex-shrink-0">
                        {task.projectName}
                      </span>
                    </div>
                    
                    {/* Task Description */}
                    {task.description && (
                      <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                        {task.description}
                      </p>
                    )}
                    
                    {/* Task Metadata */}
                    <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                      <span>
                        {task.prompts?.length || 0} prompt{task.prompts?.length !== 1 ? 's' : ''}
                      </span>
                      
                      {task.dependencies && task.dependencies.length > 0 && (
                        <span>
                          {task.dependencies.length} dependenc{task.dependencies.length !== 1 ? 'ies' : 'y'}
                        </span>
                      )}
                      
                      {task.lastResponse && (
                        <span className="text-green-600">
                          Completed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
